import { Dispatch, SetStateAction, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import axios from '../axios'
import { ENDPOINTS } from '../constants'

export interface IUseReCaptcha {
  captchaRef: React.RefObject<ReCAPTCHA>
  isVerifiedFromClient: boolean
  isClientVerificationInProgress: boolean
  isVerifiedFromServer: boolean
  isServerVerificationInProgress: boolean
  errorMessage: string
  resetCaptchaState: (errMessage?: string) => void
  onVerify: (token: string | null) => void
  onExpiry: () => void
  verifyFromServer: (token: string) => Promise<boolean>
}

export interface IUseReCaptchaOptions {
  customErrorSetter?:
    | Dispatch<SetStateAction<string>>
    | ((message: string) => void)
}

export default function useReCaptcha({
  customErrorSetter,
}: IUseReCaptchaOptions = {}): IUseReCaptcha {
  const captchaRef = useRef<ReCAPTCHA>(null)

  const [isVerifiedFromClient, setIsVerifiedFromClient] = useState(false)

  const [
    isClientVerificationInProgress,
    setIsClientVerificationInProgress,
  ] = useState(false)

  const [isVerifiedFromServer, setIsVerifiedFromServer] = useState(false)

  const [
    isServerVerificationInProgress,
    setIsServerVerificationInProgress,
  ] = useState(false)

  const [errorMessage, setErrorMessage] = useState('')

  const updateErrorMessage = (message: string) => {
    setErrorMessage(message)
    customErrorSetter?.(message)
  }

  const resetCaptchaState = (errMessage?: string) => {
    if (captchaRef.current) {
      captchaRef.current.reset()
    }

    setIsVerifiedFromClient(false)

    setIsVerifiedFromServer(false)

    if (errMessage) {
      updateErrorMessage(errMessage)
    }
  }

  const onVerify = (token: string | null) => {
    setIsClientVerificationInProgress(true)

    if (token === null) {
      resetCaptchaState('Verification failed, please try again!')
      setIsClientVerificationInProgress(false)

      return
    }

    updateErrorMessage('')
    setIsVerifiedFromClient(true)

    setIsClientVerificationInProgress(false)

    verifyFromServer(token)
  }

  const onExpiry = () => {
    resetCaptchaState('Captcha token expired, please try again!')
  }

  const verifyFromServer = async (token: string): Promise<boolean> => {
    setIsServerVerificationInProgress(true)

    try {
      const response = await axios.post(ENDPOINTS.verifyCaptcha, { token })

      if (!response?.data?.data?.success) {
        throw new Error('Captcha token not verified from server!')
      }

      setIsVerifiedFromServer(true)

      return true
    } catch (error) {
      console.error(
        'Error verifying captcha from server:',
        (error as Error).message
      )

      resetCaptchaState('Verification failed, please try again!')

      return false
    } finally {
      setIsServerVerificationInProgress(false)
    }
  }

  return {
    captchaRef,
    isVerifiedFromClient,
    isClientVerificationInProgress,
    isVerifiedFromServer,
    isServerVerificationInProgress,
    errorMessage,
    resetCaptchaState,
    onVerify,
    onExpiry,
    verifyFromServer,
  }
}
